import { useStrings } from "@1js/localization-components";
import type { Prompt } from "@1js/pl-card-components";
import {
  ConditionalWrap,
  ExtensionAuthor,
  PromptAuthor,
} from "@1js/pl-card-components";
import type { ExtensionPrompt, Group } from "@1js/pl-types";
import {
  isExtensionPrompt,
  isUserCreatedExtensionPrompt,
  isUserPrompt,
} from "@1js/pl-utils";
import type { ToastIntent } from "@fluentui/react-components";
import {
  Text,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { useFlightContext } from "../../../hooks/useFlightContext";
import { PropertyContext } from "../../../pages/PropertyContext.web";
import { MetaOSAppContext } from "../../../pages/metaos/MetaOSAppContext";
import type { WebPrompt } from "../../data";
import { MoreLikeThis } from "../MoreLikeThis.web";
import { PromptDescription } from "../PromptDescription.web";
import PromptDetailSignIn from "../PromptDetailSignIn.web";
import { Detail } from "./Detail.web";
import { MakeItYourOwn } from "./MakeItYourOwn";
import { AuthorLabel } from "./PromptPreview.web.string";

const useStyles = makeStyles({
  mainContainer: {
    display: "flex",
    width: "725px",
    flexDirection: "column",
    ...shorthands.gap("16px"),
    "@media (max-width: 950px)": {
      width: "auto",
      justifyContent: "center",
      alignItems: "flex-start",
      alignSelf: "stretch",
      ...shorthands.padding("0px"),
      ...shorthands.margin("2px", "2px"),
    },
  },

  authorContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    alignSelf: "stretch",
    ...shorthands.gap("8px"),
  },

  author: {
    color: tokens.colorNeutralForeground2,
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "1.375rem",
    alignContent: "end",
  },

  row: {
    display: "flex",
    flexDirection: "row",
    gap: "24px",
    "@media (max-width: 540px)": {
      flexDirection: "column",
    },
  },
});

type PromptPreviewProps = {
  prompt: WebPrompt | Prompt;
  morePrompts: Prompt[];
  isBookmarked?: boolean;
  isBookmarkEnabled?: boolean;
  isAgentPromptSavingEnabled?: boolean;
  isPromptSavingEnabled?: boolean;
  isPromptSharingEnabled?: boolean;
  isPromptSharingViaEmailEnabled?: boolean;
  isShowPromptAuthor?: boolean;
  isPromptCopyLinkEnabled?: boolean;
  isPromptLikingEnabled?: boolean;
  onBookmarkToggled?: (
    prompt: Prompt,
    value?: boolean,
    toastNotify?: (
      intent: ToastIntent,
      title: string,
      body?: string,
      footer?: JSX.Element,
      headerButton?: JSX.Element
    ) => void
  ) => Promise<void>;
  onShareClicked?: (prompt: Prompt) => void;
  onCopyClicked?: (prompt: Prompt) => void;
  onMoreLikeThisClicked?: (prompt: Prompt) => void;
  onDelete?: (prompt: Prompt) => void;
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  isPromptSharingToWorkgroupEnabled?: boolean;
  onShareToWorkgroupClicked?: (prompts: Prompt[]) => void | Promise<void>;
  groups?: Group[];
  selectedGroupId?: string;
  isPromptRemovingFromWorkgroupEnabled?: boolean;
  onRemoveFromWorkgroupClicked?: (
    prompts: Prompt[],
    groupIds: string[]
  ) => void;
};

export const PromptPreview: React.FC<PromptPreviewProps> = ({
  prompt,
  morePrompts,
  isBookmarked,
  isBookmarkEnabled,
  isAgentPromptSavingEnabled,
  isPromptSavingEnabled,
  isPromptSharingEnabled,
  isPromptSharingViaEmailEnabled,
  isShowPromptAuthor,
  isPromptCopyLinkEnabled,
  isPromptLikingEnabled,
  onBookmarkToggled,
  onShareClicked,
  onCopyClicked,
  onMoreLikeThisClicked,
  onDelete,
  onTryInAppBadgeAction,
  isPromptSharingToWorkgroupEnabled,
  onShareToWorkgroupClicked,
  groups,
  selectedGroupId,
  isPromptRemovingFromWorkgroupEnabled,
  onRemoveFromWorkgroupClicked,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    AuthorLabel,
  });
  const isExtension = isExtensionPrompt(prompt);
  const isUserExtensionPrompt = isUserCreatedExtensionPrompt(prompt);
  const metaOSAppContext = React.useContext(MetaOSAppContext);

  const showMakeItYourOwn = !!(
    !isExtension &&
    prompt.Origin !== "User" &&
    prompt.DisplayDescription &&
    prompt.DisplayDescription.length > 0
  );
  const showMoreLikeThis = !isExtension;
  const { isFlightActive } = useFlightContext();
  const [siteSetting] = React.useContext(PropertyContext);
  const isGoBoldEnabled = isFlightActive("isGoBoldPromptCardEnabled");
  const shouldShowAuthor =
    (isPromptSavingEnabled || isShowPromptAuthor) &&
    !(isUserPrompt(prompt) && !prompt.Author);

  return (
    <div className={styles.mainContainer}>
      {!isGoBoldEnabled && shouldShowAuthor && (
        <div
          className={styles.authorContainer}
          data-testid={"prompt-preview-author"}
        >
          <Text className={styles.author}>{localizations.AuthorLabel()}</Text>
          {!isExtension || isUserExtensionPrompt ? (
            <PromptAuthor prompt={prompt} expanded={true} />
          ) : (
            <ExtensionAuthor prompt={prompt as ExtensionPrompt} />
          )}
        </div>
      )}
      <ConditionalWrap
        condition={isGoBoldEnabled}
        wrapIfTrue={(children) => <div className={styles.row}>{children}</div>}
      >
        <Detail
          prompt={prompt}
          isBookmarkEnabled={isBookmarkEnabled}
          isAgentPromptSavingEnabled={isAgentPromptSavingEnabled}
          isPromptSavingEnabled={isPromptSavingEnabled}
          isPromptSharingEnabled={isPromptSharingEnabled}
          isPromptSharingViaEmailEnabled={isPromptSharingViaEmailEnabled}
          isBookmarked={isBookmarked}
          isPromptCopyLinkEnabled={isPromptCopyLinkEnabled}
          isPromptLikingEnabled={isPromptLikingEnabled}
          onBookmarkToggled={onBookmarkToggled}
          onShareClicked={onShareClicked}
          onCopyClicked={onCopyClicked}
          onDelete={onDelete}
          onTryInAppBadgeAction={onTryInAppBadgeAction}
          isPromptSharingToWorkgroupEnabled={isPromptSharingToWorkgroupEnabled}
          onShareToWorkgroupClicked={onShareToWorkgroupClicked}
          groups={groups}
          selectedGroupId={selectedGroupId}
          onRemoveFromWorkgroupClicked={onRemoveFromWorkgroupClicked}
          isPromptRemovingFromWorkgroupEnabled={
            isPromptRemovingFromWorkgroupEnabled
          }
        />
        <PromptDescription
          prompt={prompt}
          shouldShowAuthor={!!shouldShowAuthor}
        />
      </ConditionalWrap>
      {isFlightActive("isSignInSuggestionEnabled") &&
        !metaOSAppContext.isMosApp &&
        !siteSetting.user && <PromptDetailSignIn promptId={prompt.PromptId} />}
      {showMakeItYourOwn && prompt.DisplayDescription && (
        <MakeItYourOwn text={prompt.DisplayDescription} />
      )}
      {showMoreLikeThis && (
        // TODO: More like should reflect prompts of same extension. For now we're not showing
        <MoreLikeThis
          prompts={morePrompts}
          onClicked={onMoreLikeThisClicked}
          onBookmarkToggled={onBookmarkToggled}
        />
      )}
    </div>
  );
};
